import type { ComponentProps, ReactNode } from 'react'
import type { VariantProps } from 'class-variance-authority'

import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

import {
    StatusActiveIcon,
    StatusApprovedIcon,
    StatusDraftIcon,
    StatusInactiveIcon,
    StatusScoreIcon
} from '@/components/icons/goodunited-icons'

/*
 * GoodUnited / Badge. Figma: 10 / Components / Badge.
 * variant: success (Colors/status/success), info (Colors/status/info), secondary (neutral counts).
 * shape: soft (4px corners, labels and counts) or pill (status with icon).
 * Text is Roboto / text-sm / medium.
 */
const badgeVariants = cva(
    'inline-flex w-fit shrink-0 items-center justify-center gap-1 px-2 py-0.5 text-sm font-medium whitespace-nowrap [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                success: 'bg-status-success-background text-status-success-foreground',
                info: 'bg-status-info-background text-status-info-foreground',
                secondary: 'bg-surface-muted text-text-secondary'
            },
            shape: {
                soft: 'rounded-sm',
                pill: 'rounded-full'
            }
        },
        defaultVariants: {
            variant: 'secondary',
            shape: 'soft'
        }
    }
)

type BadgeProps = ComponentProps<'span'> & VariantProps<typeof badgeVariants>

function Badge({ className, variant, shape, ...props }: BadgeProps) {
    return (
        <span
            data-slot='badge'
            data-variant={variant}
            className={cn(badgeVariants({ variant, shape }), className)}
            {...props}
        />
    )
}

type BadgeStatusValue = 'draft' | 'active' | 'approved' | 'inactive' | 'no-budget'

const statusConfig: Record<BadgeStatusValue, { label: string, variant: 'success' | 'info' | 'secondary', icon: ReactNode }> = {
    'draft': { label: 'Draft', variant: 'info', icon: <StatusDraftIcon /> },
    'active': { label: 'Active', variant: 'success', icon: <StatusActiveIcon /> },
    'approved': { label: 'Approved', variant: 'success', icon: <StatusApprovedIcon /> },
    'inactive': { label: 'Inactive', variant: 'secondary', icon: <StatusInactiveIcon /> },
    'no-budget': { label: 'No budget', variant: 'secondary', icon: <StatusInactiveIcon /> }
}

interface BadgeStatusProps extends Omit<ComponentProps<'span'>, 'children'> {
    status: BadgeStatusValue
    label?: string
}

/* Badge/Status: each GoodUnited domain status with its label and original icon. */
function BadgeStatus({ status, label, className, ...props }: BadgeStatusProps) {
    const config = statusConfig[status]

    return (
        <Badge variant={config.variant} shape='pill' data-status={status} className={className} {...props}>
            {config.icon}
            {label ?? config.label}
        </Badge>
    )
}

interface ScoreBadgeProps extends Omit<ComponentProps<'span'>, 'children'> {
    value: number | string
}

/* ScoreBadge: a supporter intent score. */
function ScoreBadge({ value, className, ...props }: ScoreBadgeProps) {
    return (
        <span
            data-slot='score-badge'
            className={cn('inline-flex w-fit items-center gap-0.5 rounded-sm bg-status-info-background p-1 text-sm font-medium text-status-info-foreground [&_svg]:size-4', className)}
            {...props}
        >
            <StatusScoreIcon />
            {value}
        </span>
    )
}

export { Badge, BadgeStatus, ScoreBadge, badgeVariants }
export type { BadgeStatusValue }
